<template>
  <div class="feature-container">
    <!-- User Controls -->
    <div class="controls" style="position: sticky; top: 0; z-index: 1000;">
      <div class="left-controls">
        <button class="refresh-button" @click="refreshData" title="Refresh" :disabled="!selectedRailObject">
          <span class="material-icons">refresh</span>
        </button>
        <button class="refresh-button" @click="downloadData" title="Download" :disabled="downloading || !selectedRailObject">
          <span class="material-icons" :class="{ 'loading': downloading }">download</span>
        </button>
        <p>{{ $t('dashboard.updated') }} {{ formattedLastUpdated }}</p>
      </div>
      <div class="right-controls">
        <select
          v-model="selectedRailObject"
          class="select-s2 browser-default small-contents"
          @change="loadFeatureData"
        >
          <option value="">{{ $t('dashboard.selectanobject') }} </option>
          <option
            v-for="asset in dropdownOptions"
            :key="asset.RailObjectId"
            :value="asset.RailObjectId"
          >
            {{ asset.LocationName }} - {{ asset.AssetName }}
          </option>
        </select>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="error-message">
      <span class="error-icon">⚠️</span> {{ errorMessage }}
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="spinner-container">
      <div class="spinner"></div>
    </div>

    <!-- No Data Message -->
    <div v-else-if="!errorMessage && (!processedData || processedData.length === 0)">
      {{ $t('dashboard.feature1nodata') }}
    </div>

    <!-- Table with Pagination -->
    <div v-else class="table-container">
      <div class="table-wrapper">
        <table class="kvb-table">
          <thead>
            <tr>
              <th>{{ $t('dashboard.datetime') }}</th>
              <th>{{ $t('views.navbar.asset') }}</th>
              <th>{{ $t('views.alarmlist.location') }}</th>
              <th>{{ $t('charts.n') }}/{{ $t('charts.r') }}</th>
              <th>{{ $t('dashboard.duration') }}</th>
              <th>{{ $t('dashboard.force') }} {{ $t('charts.n') }} (max) ({{ paginatedData.length > 0 ? paginatedData[0].ForceUnit : '' }})</th>
              <th>{{ $t('dashboard.force') }} {{ $t('charts.r') }} (max) ({{ paginatedData.length > 0 ? paginatedData[0].ForceUnit : '' }})</th>
              <th>{{ $t('dashboard.gap') }} {{ $t('charts.n') }} (min) ({{ paginatedData.length > 0 ? paginatedData[0].GapUnit : '' }})</th>
              <th>{{ $t('dashboard.gap') }} {{ $t('charts.r') }} (min) ({{ paginatedData.length > 0 ? paginatedData[0].GapUnit : '' }})</th>
              <th>{{ $t('views.switch.temperature') }} (°C)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in paginatedData" :key="entry.RailObjectId + entry.TimeStamp">
              <td @click="goToMeasurement(entry.RailObjectId, entry.MeasurementId)"
                :class="{ 'clickable': entry.MeasurementId }">
                {{ formatDate(entry.TimeStamp) }}
              </td>
              <td>{{ entry.Assetname || '-' }}</td>
              <td>{{ entry.LocationName || '-' }}</td>
              <td>{{ entry.Direction === 0 ? $t('charts.n') : $t('charts.r') }}</td>
              <td>{{ entry.Duration }}</td>
              <td>{{ entry.MaxForceL.toFixed(2) }}</td>
              <td>{{ entry.MaxForceR.toFixed(2) }}</td>
              <td>{{ entry.MaxGapL.toFixed(2) }}</td>
              <td>{{ entry.MaxGapR.toFixed(2) }}</td>
              <td>{{ entry.Temperature !== '-' ? entry.Temperature.toFixed(1) : '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Controls -->
      <div v-if="paginatedData.length > 0" class="pagination">
        <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
          <span class="material-icons">navigate_before</span>
        </button>
        <span>{{ $t('dashboard.page') }} {{ currentPage }} {{ $t('dashboard.of') }} {{ totalPages }}</span>
        <button @click="nextPage" :disabled="currentPage >= totalPages" class="pagination-btn">
          <span class="material-icons">navigate_next</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import AuthenticationService from '@/services/AuthenticationService'
import DashboardService from '@/services/DashboardService'

export default {
  name: 'FeatureKVB',
  props: ['companyId'],
  data () {
    return {
      loading: false,
      downloading: false,
      currentUser: AuthenticationService.getUser(),
      errorMessage: null,
      featureData: [],
      logicalAssets: [],
      processedData: [],
      dropdownOptions: [],
      selectedRailObject: '',
      currentPage: 1,
      rowsPerPage: 5,
      featureId: 1,
      lastUpdated: null
    }
  },
  mounted () {
    window.addEventListener('resize', this.updateRowsPerPage)
    this.fetchInitialData(this.companyId)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateRowsPerPage)
  },
  computed: {
    formattedLastUpdated () {
      if (!this.lastUpdated) return 'N/A'
      const date = new Date(this.lastUpdated)
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },

    paginatedData () {
      const start = (this.currentPage - 1) * this.rowsPerPage
      const end = start + this.rowsPerPage
      return this.processedData.slice(start, end)
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.processedData.length / this.rowsPerPage))
    }
  },
  methods: {
    async fetchInitialData (companyId) {
      this.loading = true
      this.errorMessage = null
      try {
        const [logicalAssetsResponse] = await Promise.all([
          DashboardService.getLogicalAssets(this.currentUser.UserId, companyId, this.featureId)
        ])
        this.logicalAssets = logicalAssetsResponse.data
        this.processDropdownOptions()
      } catch (error) {
        this.errorMessage = error.message || 'Failed to load initial data. Please try again.'
        this.logicalAssets = []
        this.dropdownOptions = []
      }
      this.loading = false
    },

    async loadFeatureData () {
      if (!this.companyId || !this.selectedRailObject) {
        this.featureData = []
        this.processedData = []
        this.lastUpdated = null
        this.currentPage = 1
        return
      }

      this.loading = true
      this.errorMessage = null
      try {
        const featureResponse = await DashboardService.getFeatureData(
          this.currentUser.UserId,
          this.companyId,
          this.featureId,
          this.selectedRailObject
        )
        this.featureData = featureResponse.data
        this.processFeatureData()
        this.lastUpdated = new Date()
      } catch (error) {
        this.errorMessage = error.message || 'Failed to load feature data. Please try again.'
        this.featureData = []
        this.processedData = []
      }
      this.loading = false
      this.$nextTick(() => {
        this.updateRowsPerPage()
      })
    },

    processDropdownOptions () {
      if (!this.logicalAssets || this.logicalAssets.length === 0) {
        this.dropdownOptions = []
        return
      }
      this.dropdownOptions = this.logicalAssets
    },

    processFeatureData () {
      if (!this.featureData || this.featureData.length === 0) {
        this.processedData = []
        return
      }

      this.processedData = this.featureData.map(entry => ({
        ...entry,
        TimeStamp: entry.TimeStamp || '-',
        MaxForceL: entry.MaxForceL || 0,
        MaxForceR: entry.MaxForceR || 0,
        MaxGapL: entry.MaxGapL || 0,
        MaxGapR: entry.MaxGapR || 0,
        Temperature: entry.Temperature || '-',
        RailObjectId: entry.RailObjectId
      }))
    },

    formatDate (dateStr) {
      if (!dateStr || dateStr.includes('0001-01-01')) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
        ' ' +
        date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    },

    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    prevPage () {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },

    updateRowsPerPage () {
      this.$nextTick(() => {
        const tableWrapper = this.$el.querySelector('.table-wrapper')
        const tableHeader = this.$el.querySelector('.kvb-table thead')
        if (tableWrapper && tableHeader) {
          const rowHeight = 32 // Row height
          const headerHeight = tableHeader.clientHeight || 40 // Header height
          const paginationHeight = 43 // Pagination controls

          // Calculate available height for table rows
          const availableHeight = tableWrapper.clientHeight - headerHeight - paginationHeight

          // Calculate max number of rows that fit
          this.rowsPerPage = Math.max(1, Math.floor(availableHeight / rowHeight))

          // Ensure pagination updates
          if (this.currentPage > this.totalPages) {
            this.currentPage = Math.max(1, this.totalPages)
          }
        }
      })
    },

    async refreshData () {
      if (this.companyId && this.selectedRailObject) {
        this.currentPage = 1
        await this.loadFeatureData()
      }
    },

    goToMeasurement (railObjectId, measurementId) {
      this.$router.push(`/switch/${railObjectId}?measurementId=${measurementId}`)
    },

    async downloadData () {
      if (this.downloading || !this.selectedRailObject) return
      this.downloading = true
      try {
        const response = await DashboardService.getFeaturesExport(
          this.currentUser.UserId,
          this.companyId,
          this.featureId,
          this.selectedRailObject,
          { responseType: 'blob' }
        )
        const baseFilename = 'feature_export'
        const timestamp = new Date().toISOString().replace(/[:.-]/g, '').slice(0, 14)
        const companyIdOrName = this.companyId || 'Unknown'
        const filename = `${baseFilename}_${companyIdOrName}_${timestamp}.xlsx`
        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.errorMessage = 'Failed to download the file'
      } finally {
        this.downloading = false
      }
    }
  }
}
</script>

<style scoped>
/* Main container */
.feature-container {
  background: white;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Table */
.table-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-wrapper {
  flex-grow: 1;
  overflow-y: auto;
}

.kvb-table td.clickable {
  cursor: pointer;
}

.kvb-table td.clickable:hover {
  color: #1e6bb8;
}

.kvb-table {
  width: 100%;
  border-collapse: collapse;
}

.kvb-table th, .kvb-table td {
  border: 1px solid #ccc;
  padding: 5px;
  text-align: center;
}

.kvb-table th {
  background-color: #f4f4f4;
}

/* Colors */
.bg-red {
  background-color: rgba(255, 0, 0, 0.432);
}

.bg-orange {
  background-color: rgba(255, 165, 0, 0.3);
}

.bg-green {
  background-color: rgba(0, 255, 0, 0.3);
}

/* Add User Controls Styles */
.controls {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: 12px;
  height: 34px;
  flex-wrap: nowrap;
}

.left-controls {
  display: flex;
  gap: 5px;
  align-items: center;
}

.right-controls {
  display: flex;
  gap: 5px;
  align-items: center;
  position: relative;
}

.controls .refresh-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  height: 24px;
  line-height: 24px;
}

.controls .refresh-button .material-icons {
  font-size: 20px;
  color: #666;
}

.controls .refresh-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Pagination */
.pagination {
  position: sticky;
  bottom: 4px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 0;
  height: 24px;
  box-sizing: border-box;
  z-index: 1;
}

.pagination-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  line-height: 24px;
  transition: color 0.3s ease;
}

.pagination-btn:disabled {
  opacity: 0.5;
}

.pagination-btn .material-icons {
  font-size: 24px;
  color: #666;
}

.pagination-btn:hover:not(:disabled) .material-icons {
  color: #3498db;
}

/* Dropdown */
.select-s2.browser-default.small-contents {
  padding: 2px 4px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 24px;
  width: auto;
  min-width: 50px;
  max-width: 300px;
  background: white;
  cursor: pointer;
}

.select-s2.browser-default.small-contents:focus {
  outline: none;
  border-color: #3498db;
}

/* Pagination */
.pagination {
  position: sticky;
  bottom: 4px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 0;
  height: 24px;
  box-sizing: border-box;
  z-index: 1;
}

/* Spinner */
.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  z-index: 10;
}

.spinner {
  border: 6px solid #f3f3f3;
  border-top: 6px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  background-color: rgba(255, 0, 0, 0.1);
  color: #d32f2f;
  border: 1px solid #d32f2f;
  padding: 10px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 500;
  margin: 10px;
  width: fit-content;
  max-width: 100%;
  text-align: center;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  animation: fadeIn 0.5s ease-in-out;
}

.error-icon {
  margin-right: 8px;
  font-size: 20px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard3/_FeatureKVB.vue